import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Plus, Phone, ChevronRight, Trash2, ArrowRightLeft, Download } from 'lucide-react';
import { toast } from 'sonner';

const typeBadge = (t) => {
  const m = { regular: 'border-blue-200 text-blue-700 bg-blue-50', visitor: 'border-amber-200 text-amber-700 bg-amber-50', occasional: 'border-teal-200 text-teal-700 bg-teal-50', inactive: 'border-slate-300 text-slate-500 bg-slate-50' };
  return m[t] || 'border-slate-200 text-slate-600';
};

export default function People() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [people, setPeople] = useState([]);
  const [families, setFamilies] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [ministryFilter, setMinistryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', secondary_phone: '', email_address: '', address: '', city: '', state: '', zip_code: '', birthday: '', anniversary: '', family_id: '', person_type: 'visitor', ministries: [] });
  const [selected, setSelected] = useState(new Set());
  const [massDeleteOpen, setMassDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteStep, setDeleteStep] = useState(1);
  const [typeChangeOpen, setTypeChangeOpen] = useState(false);
  const [newType, setNewType] = useState('regular');
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFields, setExportFields] = useState(['first_name','last_name','phone','email_address','address','city','state','zip_code','birthday','person_type','ministries']);

  useEffect(() => {
    Promise.all([api.get('/people'), api.get('/families'), api.get('/ministries')])
      .then(([p, f, m]) => { setPeople(p.data); setFamilies(f.data); setMinistries(m.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => people.filter(p => {
    if (search) {
      const q = search.toLowerCase();
      if (!p.first_name.toLowerCase().includes(q) && !p.last_name.toLowerCase().includes(q) && !p.phone?.toLowerCase().includes(q)) return false;
    }
    if (typeFilter !== 'all' && p.person_type !== typeFilter) return false;
    if (ministryFilter !== 'all' && !p.ministries?.includes(ministryFilter)) return false;
    return true;
  }), [people, search, typeFilter, ministryFilter]);

  const toggleMinistry = (name) => {
    setForm(f => ({
      ...f,
      ministries: f.ministries.includes(name) ? f.ministries.filter(m => m !== name) : [...f.ministries, name]
    }));
  };

  const handleAdd = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error('Name is required'); return; }
    try {
      const res = await api.post('/people', form);
      setPeople(prev => [...prev, res.data]);
      setAddOpen(false);
      setForm({ first_name: '', last_name: '', phone: '', secondary_phone: '', email_address: '', address: '', city: '', state: '', zip_code: '', birthday: '', anniversary: '', family_id: '', person_type: 'visitor', ministries: [] });
      toast.success('Person added');
    } catch { toast.error('Failed to add person'); }
  };

  const toggleSelect = (pid) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(pid) ? next.delete(pid) : next.add(pid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(p => p.person_id)));
    }
  };

  const handleMassDelete = async () => {
    if (confirmText !== 'CONFIRM DELETE') { toast.error("Type 'CONFIRM DELETE' to proceed"); return; }
    try {
      const res = await api.post('/people/mass-delete', { person_ids: [...selected], confirmation: confirmText });
      setPeople(prev => prev.filter(p => !selected.has(p.person_id)));
      setSelected(new Set());
      setMassDeleteOpen(false);
      setConfirmText('');
      setDeleteStep(1);
      toast.success(`${res.data.count} people archived`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleMassTypeChange = async () => {
    try {
      const res = await api.post('/people/mass-type-change', { person_ids: [...selected], new_type: newType });
      setPeople(prev => prev.map(p => selected.has(p.person_id) ? { ...p, person_type: newType } : p));
      setSelected(new Set());
      setTypeChangeOpen(false);
      toast.success(`${res.data.count} people moved to ${newType}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleExport = async () => {
    try {
      const res = await api.post('/people/export', { fields: exportFields }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'PastorOS_People_Export.csv';
      a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const getFamilyName = (fid) => families.find(f => f.family_id === fid)?.family_name || '';

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="people-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or phone..." className="pl-10 border-slate-300" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="type-filter" className="w-36 border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="regular">Regular</SelectItem>
              <SelectItem value="visitor">Visitor</SelectItem>
              <SelectItem value="occasional">Occasional</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ministryFilter} onValueChange={setMinistryFilter}>
            <SelectTrigger data-testid="ministry-filter" className="w-44 border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ministries</SelectItem>
              {ministries.map(m => <SelectItem key={m.ministry_id} value={m.name}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {selected.size > 0 && (user?.role === 'admin' || user?.role === 'pastor') && (
            <>
              <Button data-testid="mass-type-change-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => setTypeChangeOpen(true)}>
                <ArrowRightLeft className="h-4 w-4 mr-1" />Change Type ({selected.size})
              </Button>
              <Button data-testid="mass-delete-button" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => { setMassDeleteOpen(true); setDeleteStep(1); setConfirmText(''); }}>
                <Trash2 className="h-4 w-4 mr-1" />Delete ({selected.size})
              </Button>
            </>
          )}
          <Button data-testid="export-button" variant="outline" className="border-slate-300" onClick={() => setExportOpen(true)}>
            <Download className="h-4 w-4 mr-1" />Export
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-person-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="h-4 w-4 mr-1" />Add Person</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-['Outfit']">Add New Person</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div><Label>First Name *</Label><Input data-testid="add-first-name" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="mt-1" /></div>
                <div><Label>Last Name</Label><Input data-testid="add-last-name" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="mt-1" /></div>
                <div><Label>Phone</Label><Input data-testid="add-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" /></div>
                <div><Label>Secondary Phone</Label><Input data-testid="add-secondary-phone" value={form.secondary_phone} onChange={e => setForm(f => ({ ...f, secondary_phone: e.target.value }))} className="mt-1" /></div>
                <div className="col-span-2"><Label>Email Address</Label><Input data-testid="add-email" type="email" value={form.email_address} onChange={e => setForm(f => ({ ...f, email_address: e.target.value }))} className="mt-1" /></div>
                <div className="col-span-2"><Label>Address</Label><Input data-testid="add-address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="mt-1" /></div>
                <div><Label>City</Label><Input data-testid="add-city" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>State</Label><Input data-testid="add-state" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Zip</Label><Input data-testid="add-zip" value={form.zip_code} onChange={e => setForm(f => ({ ...f, zip_code: e.target.value }))} className="mt-1" /></div>
                </div>
                <div><Label>Birthday</Label><Input data-testid="add-birthday" type="date" value={form.birthday} onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))} className="mt-1" /></div>
                <div><Label>Anniversary</Label><Input data-testid="add-anniversary" type="date" value={form.anniversary} onChange={e => setForm(f => ({ ...f, anniversary: e.target.value }))} className="mt-1" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.person_type} onValueChange={v => setForm(f => ({ ...f, person_type: v }))}>
                    <SelectTrigger data-testid="add-type" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visitor">Visitor</SelectItem>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="occasional">Occasional</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Family</Label>
                  <Select value={form.family_id || 'none'} onValueChange={v => setForm(f => ({ ...f, family_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="add-family" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Family</SelectItem>
                      {families.map(f => <SelectItem key={f.family_id} value={f.family_id}>{f.family_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Ministries</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {ministries.map(m => (
                      <label key={m.ministry_id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm ${form.ministries.includes(m.name) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                        <Checkbox checked={form.ministries.includes(m.name)} onCheckedChange={() => toggleMinistry(m.name)} className="h-3.5 w-3.5" />
                        {m.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <Button data-testid="add-person-submit" onClick={handleAdd} className="w-full mt-2 bg-blue-700 hover:bg-blue-800">Add Person</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200">
                  {(user?.role === 'admin' || user?.role === 'pastor') && (
                    <TableHead className="w-10">
                      <Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleSelectAll} className="border-slate-300" />
                    </TableHead>
                  )}
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500">Last Name</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500">First Name</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500 hidden sm:table-cell">Phone</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500 hidden md:table-cell">Family</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500">Status</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500 hidden lg:table-cell">Ministries</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(p => (
                  <TableRow key={p.person_id} data-testid={`person-row-${p.person_id}`} className="cursor-pointer hover:bg-slate-50 border-slate-100 transition-colors">
                    {(user?.role === 'admin' || user?.role === 'pastor') && (
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Checkbox data-testid={`select-${p.person_id}`} checked={selected.has(p.person_id)} onCheckedChange={() => toggleSelect(p.person_id)} className="border-slate-300" />
                      </TableCell>
                    )}
                    <TableCell onClick={() => navigate(`/people/${p.person_id}`)}>
                      <div className="flex items-center gap-3">
                        {p.photo_path ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.photo_path}`} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600 shrink-0">{p.first_name[0]}{(p.last_name || '')[0] || ''}</div>
                        )}
                        <span className="text-sm font-medium text-slate-900">{p.last_name || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/people/${p.person_id}`)}>
                      <span className="text-sm text-slate-700">{p.first_name}</span>
                      <p className="text-xs text-slate-500 sm:hidden">{p.phone}</p>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell" onClick={() => navigate(`/people/${p.person_id}`)}>
                      <span className="text-sm text-slate-600 flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone || '—'}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell" onClick={() => navigate(`/people/${p.person_id}`)}>
                      <span className="text-sm text-slate-600">{getFamilyName(p.family_id) || '—'}</span>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/people/${p.person_id}`)}>
                      <Badge variant="outline" className={`text-xs ${typeBadge(p.person_type)}`}>{p.person_type}</Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell" onClick={() => navigate(`/people/${p.person_id}`)}>
                      <div className="flex gap-1 flex-wrap">
                        {p.ministries?.slice(0, 2).map(m => <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>)}
                        {p.ministries?.length > 2 && <Badge variant="secondary" className="text-xs">+{p.ministries.length - 2}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/people/${p.person_id}`)}><ChevronRight className="h-4 w-4 text-slate-400" /></TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-slate-500">No people found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={massDeleteOpen} onOpenChange={setMassDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Outfit']">
              {deleteStep === 1 ? 'Archive Selected People?' : 'Final Confirmation Required'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteStep === 1 ? (
                <span>You are about to archive <strong>{selected.size} people</strong>. Their records will be preserved but they won't appear in active lists. This requires double confirmation.</span>
              ) : (
                <div className="space-y-3">
                  <p>Type <strong>CONFIRM DELETE</strong> below to proceed:</p>
                  <Input data-testid="mass-delete-confirm-input" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Type CONFIRM DELETE" className="border-slate-300" />
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteStep(1); setConfirmText(''); }}>Cancel</AlertDialogCancel>
            {deleteStep === 1 ? (
              <AlertDialogAction data-testid="mass-delete-step1" onClick={(e) => { e.preventDefault(); setDeleteStep(2); }} className="bg-red-600 hover:bg-red-700">
                Continue to Confirm
              </AlertDialogAction>
            ) : (
              <Button data-testid="mass-delete-step2" onClick={handleMassDelete} disabled={confirmText !== 'CONFIRM DELETE'} className="bg-red-600 hover:bg-red-700 disabled:opacity-50">
                Archive {selected.size} People
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={typeChangeOpen} onOpenChange={setTypeChangeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Outfit']">Change Type for {selected.size} People</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3 pt-2">
                <p>Move the selected people to a new type:</p>
                <Select value={newType} onValueChange={setNewType}>
                  <SelectTrigger data-testid="mass-type-select" className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="visitor">Visitor</SelectItem>
                    <SelectItem value="occasional">Occasional</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-type-change" onClick={handleMassTypeChange} className="bg-blue-700 hover:bg-blue-800">
              Move to {newType}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Outfit']">Export People</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Select which fields to include in the CSV export:</p>
          <div className="grid grid-cols-2 gap-2 pt-2">
            {[
              { key: 'first_name', label: 'First Name' },
              { key: 'last_name', label: 'Last Name' },
              { key: 'phone', label: 'Phone' },
              { key: 'secondary_phone', label: 'Secondary Phone' },
              { key: 'email_address', label: 'Email' },
              { key: 'address', label: 'Address' },
              { key: 'city', label: 'City' },
              { key: 'state', label: 'State' },
              { key: 'zip_code', label: 'Zip' },
              { key: 'birthday', label: 'Birthday' },
              { key: 'anniversary', label: 'Anniversary' },
              { key: 'person_type', label: 'Type' },
              { key: 'ministries', label: 'Ministries' },
              { key: 'family_id', label: 'Family' },
              { key: 'general_notes', label: 'Notes' },
            ].map(({ key, label }) => (
              <label key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${exportFields.includes(key) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                <Checkbox checked={exportFields.includes(key)} onCheckedChange={() => setExportFields(prev => prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key])} className="h-3.5 w-3.5" />
                {label}
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Button className="flex-1 bg-blue-700 hover:bg-blue-800" onClick={handleExport} data-testid="export-submit">
              <Download className="h-4 w-4 mr-1" />Download CSV
            </Button>
            <Button variant="outline" className="text-xs" onClick={() => setExportFields(['first_name','last_name','phone','secondary_phone','email_address','address','city','state','zip_code','birthday','anniversary','person_type','ministries','family_id','general_notes'])}>Select All</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
